/* eslint-disable no-unused-vars, react-hooks/exhaustive-deps */
import React, { useState, useEffect, useCallback } from 'react';
import {
  Shield, AlertTriangle, Users, Building2, MapPin, Bell,
  LogOut, QrCode, Globe, FileText, History, ChevronRight, ChevronDown, Radio, CheckCheck,
  ClipboardList, CreditCard, UserCheck, Map, Settings2, Layers, Briefcase, Pencil, Clock,
  ShieldCheck, Users2, Plug, Archive, AlertOctagon, Inbox, CheckSquare,
  BookOpen, FolderOpen, Search as SearchIcon, Sparkles
} from 'lucide-react';

import { AuthProvider, useAuth }  from './context/AuthContext';
import LoginPage                  from './pages/LoginPage';
import RegisterPage               from './pages/RegisterPage';
import PublicScanPage             from './pages/PublicScanPage';
import PublicLivePOBPage          from './pages/PublicLivePOBPage';
import PublicVisitRequestPage        from './pages/PublicVisitRequestPage';
import PublicVisitorLivePOBPage     from './pages/PublicVisitorLivePOBPage';
import PublicHSEPage               from './pages/PublicHSEPage';
import C                          from './constants/colors';

import * as companiesApi    from './api/companies';
import * as sitesApi        from './api/sites';
import * as zonesApi        from './api/zones';
import * as employeesApi    from './api/employees';
import * as alertsApi       from './api/alerts';
import * as transactionsApi from './api/transactions';
import * as eventsApi       from './api/events';
import * as scanApi         from './api/scan';
import * as visitorsApi    from './api/visitors';
import * as departmentsApi from './api/departments';
import * as rolesApi       from './api/roles';

import SidebarItem        from './components/ui/SidebarItem';
import TopBar             from './components/ui/TopBar';
import ConfirmModal       from './components/modals/ConfirmModal';
import CompanyFormModal   from './components/modals/CompanyFormModal';
import SiteFormModal      from './components/modals/SiteFormModal';
import ZoneFormModal      from './components/modals/ZoneFormModal';
import EmployeeFormModal  from './components/modals/EmployeeFormModal';
import EventModal         from './components/modals/EventModal';
import ResetAlertsModal   from './components/modals/ResetAlertsModal';
import SiteCheckInModal   from './components/modals/SiteCheckInModal';
import LivePOBModal       from './components/modals/LivePOBModal';
import CompanyMapView     from './components/views/CompanyMapView';
import CompanyScopeMapView from './components/views/CompanyScopeMapView';
import SiteScopeMapView   from './components/views/SiteScopeMapView';
import SiteView           from './components/views/SiteView';
import ZoneView           from './components/views/ZoneView';
import EmployeeDetailView from './components/views/EmployeeDetailView';
import VisitorCardDetailView from './components/views/VisitorCardDetailView';
import AlertsView         from './components/views/AlertsView';
import TransactionLogView from './components/views/TransactionLogView';
import EmployeesView      from './components/views/EmployeesView';
import HeadcountView           from './components/views/HeadcountView';
import QRScanView              from './components/views/QRScanView';
import VisitorCompaniesView    from './components/views/VisitorCompaniesView';
import VisitRequestsView       from './components/views/VisitRequestsView';
import VisitorCardsView        from './components/views/VisitorCardsView';
import SuperAdminView          from './components/views/SuperAdminView';
import DepartmentsView, { DepartmentFormModal } from './components/views/DepartmentsView';
import RolesView from './components/views/RolesView';
import TimeAttendanceView from './components/views/TimeAttendanceView';
import TeamView from './components/views/TeamView';
import HseCoursesView from './components/views/HseCoursesView';
import IncidentsView from './components/views/IncidentsView';
import ActionsView from './components/views/ActionsView';
import ActionFormModal from './components/modals/ActionFormModal';
import MyTasksView from './components/views/MyTasksView';
import AccessRightsView from './components/views/AccessRightsView';
import IntegrationsView from './components/views/IntegrationsView';
import ArchivedEmployeesView from './components/views/ArchivedEmployeesView';
import UploadManagerView from './components/views/UploadManagerView';
import HistorySearchView from './components/views/HistorySearchView';
import KBBrowserView from './components/views/KBBrowserView';
import HseAiSettingsView from './components/views/HseAiSettingsView';
import * as taApi from './api/timeAttendance';
import * as incidentsApi from './api/incidents';
import * as actionsApi from './api/actions';

// ─── GLOBAL STYLES ────────────────────────────────────────────────────────────
const STYLES = `
  @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@300;400;500;600;700&family=JetBrains+Mono:wght@400;500&display=swap');

  *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
  body {
    background: ${C.bg};
    color: ${C.textPrimary};
    font-family: 'DM Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
    -webkit-font-smoothing: antialiased;
  }
  ::-webkit-scrollbar { width: 4px; height: 4px; }
  ::-webkit-scrollbar-track { background: transparent; }
  ::-webkit-scrollbar-thumb { background: rgba(255,255,255,0.1); border-radius: 2px; }
  ::-webkit-scrollbar-thumb:hover { background: rgba(255,255,255,0.2); }

  @keyframes pulse-ring { 0% { transform: scale(0.8); opacity: 1; } 100% { transform: scale(2.4); opacity: 0; } }
  @keyframes fadeIn { from { opacity: 0; transform: translateY(8px); } to { opacity: 1; transform: translateY(0); } }
  @keyframes slideInLeft { from { opacity: 0; transform: translateX(-16px); } to { opacity: 1; transform: translateX(0); } }
  @keyframes slideInRight { from { opacity: 0; transform: translateX(16px); } to { opacity: 1; transform: translateX(0); } }
  @keyframes shimmer { 0% { background-position: -200% 0; } 100% { background-position: 200% 0; } }
  @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
  @keyframes alertPulse { 0%, 100% { box-shadow: 0 0 0 0 rgba(239,68,68,0.4); } 50% { box-shadow: 0 0 0 8px rgba(239,68,68,0); } }
  @keyframes ambientWave { 0%, 100% { opacity: 0.6; } 50% { opacity: 1; } }
  @keyframes dotBlink { 0%, 80%, 100% { opacity: 0; } 40% { opacity: 1; } }

  .fade-in { animation: fadeIn 0.25s ease forwards; }
  .slide-in-left { animation: slideInLeft 0.25s ease forwards; }
  .slide-in-right { animation: slideInRight 0.25s ease forwards; }
  .alert-pulse { animation: alertPulse 2s infinite; }
  .ambient-wave { animation: ambientWave 3s ease-in-out infinite; }
  .dot-blink-1 { animation: dotBlink 1.4s infinite ease-in-out 0s; }
  .dot-blink-2 { animation: dotBlink 1.4s infinite ease-in-out 0.2s; }
  .dot-blink-3 { animation: dotBlink 1.4s infinite ease-in-out 0.4s; }
  .spin { animation: spin 1s linear infinite; }
  .sidebar-item { transition: background 0.15s ease, border-color 0.15s ease; cursor: pointer; }
  .sidebar-item:hover { background: rgba(255,255,255,0.05) !important; }
  .sidebar-item.active { background: rgba(59,130,246,0.12) !important; }
  .stat-badge { transition: background 0.15s ease, transform 0.15s ease, border-color 0.15s ease; }
  .stat-badge.clickable:hover { transform: translateY(-1px); border-color: rgba(255,255,255,0.18) !important; }
  .alert-card { transition: background 0.15s ease, border-color 0.15s ease; }
  .alert-card:hover { background: rgba(255,255,255,0.03) !important; border-color: rgba(255,255,255,0.1) !important; }
  .btn { transition: background 0.15s ease, opacity 0.15s ease, transform 0.1s ease; cursor: pointer; user-select: none; }
  .btn:hover { opacity: 0.88; }
  .btn:active { transform: scale(0.97); }
  .mono { font-family: 'JetBrains Mono', 'Fira Code', monospace; }
`;

// ─── ROOT ─────────────────────────────────────────────────────────────────────
export default function Root() {
  useEffect(() => {
    const style = document.createElement('style');
    style.textContent = STYLES;
    document.head.appendChild(style);
    return () => document.head.removeChild(style);
  }, []);

  // Employee badge scan: ?e=EMPTQRHASH → LivePOB self-report page (no auth)
  const empHash = new URLSearchParams(window.location.search).get('e');
  if (empHash) return <PublicLivePOBPage empHash={empHash} />;

  // Zone QR scan: ?z=ZONEHASH → officer zone access page (no auth)
  const qrHash = new URLSearchParams(window.location.search).get('z');
  if (qrHash) return <PublicScanPage qrHash={qrHash} />;

  // Visitor company QR scan: ?vc=VCHASH → public VRF submission page (no auth)
  const vcHash = new URLSearchParams(window.location.search).get('vc');
  if (vcHash) return <PublicVisitRequestPage vcHash={vcHash} />;

  // Visitor card badge scan: ?vis=VISHASH → visitor LivePOB self-report page (no auth)
  const visHash = new URLSearchParams(window.location.search).get('vis');
  if (visHash) return <PublicVisitorLivePOBPage visHash={visHash} />;

  // HSE Training mobile page: ?hse=1 → officer scan + employee profile (no auth)
  const hseParam = new URLSearchParams(window.location.search).get('hse');
  if (hseParam !== null) return <PublicHSEPage />;

  return (
    <AuthProvider>
      <AppShell />
    </AuthProvider>
  );
}

function AppShell() {
  const { user, loading, logout } = useAuth();
  const [showRegister, setShowRegister] = useState(false);

  if (loading) return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh', background: C.bg }}>
      <div style={{ color: C.textMuted, fontSize: '14px' }}>Loading…</div>
    </div>
  );
  if (!user) {
    if (showRegister) return <RegisterPage onBack={() => setShowRegister(false)} />;
    return <LoginPage onRegister={() => setShowRegister(true)} />;
  }
  return <App onLogout={logout} />;
}

// ─── MAIN APP ─────────────────────────────────────────────────────────────────
function App({ onLogout }) {
  const { user, canAccess } = useAuth();

  // ── Core data state ──
  const [companies,       setCompanies]       = useState([]);
  const [sites,           setSites]           = useState([]);
  const [zones,           setZones]           = useState([]);
  const [employees,       setEmployees]       = useState([]);
  const [alerts,          setAlerts]          = useState([]);
  const [transactions,    setTransactions]    = useState([]);
  const [activeEvents,    setActiveEvents]    = useState({});
  const [dataLoading,     setDataLoading]     = useState(true);
  // VMS
  const [visitorCompanies, setVisitorCompanies] = useState([]);
  const [visitRequests,    setVisitRequests]    = useState([]);
  const [visitorCards,     setVisitorCards]     = useState([]);
  // Departments
  const [departments,      setDepartments]      = useState([]);
  // Roles
  const [roles,            setRoles]            = useState([]);
  // Time Attendance
  const [schedules,           setSchedules]           = useState([]);
  const [selectedTACompanyId,  setSelectedTACompanyId]  = useState('');
  const [selectedHseCompanyId, setSelectedHseCompanyId] = useState('');
  const [selectedArchivedCompanyId, setSelectedArchivedCompanyId] = useState('');
  // Incidents (HSE expansion)
  const [incidents,            setIncidents]            = useState([]);
  // Actions / second steps (HSE expansion)
  const [actions,              setActions]              = useState([]);

  // ── UI state ──
  const [currentView, setCurrentView]   = useState('map');
  const [selectedSiteId, setSelectedSiteId]             = useState(null);
  const [selectedCompanyMapId, setSelectedCompanyMapId] = useState(null);
  const [selectedZoneId, setSelectedZoneId]             = useState(null);
  const [selectedEmployeeId, setSelectedEmployeeId]     = useState(null);
  const [selectedVisitorCardId, setSelectedVisitorCardId] = useState(null);
  const [expandedCompanies, setExpandedCompanies]   = useState({});
  const [expandedSites, setExpandedSites]           = useState({});
  const [selectedVisitorsSiteId, setSelectedVisitorsSiteId] = useState(null);
  const [selectedIncidentsSiteId, setSelectedIncidentsSiteId] = useState(null);
  const [selectedActionsSiteId, setSelectedActionsSiteId] = useState(null);
  // Navigation-intent IDs from MyTasks → pre-select an item in the per-site view on mount
  const [pendingIncidentId, setPendingIncidentId] = useState(null);
  const [pendingActionId, setPendingActionId] = useState(null);

  // modal: { type: 'company'|'site'|'zone'|'employee'|null, data: { record, preselectedCompanyId, ... } }
  const [modal, setModal]   = useState({ type: null, data: null });
  const openModal           = (type, data = {}) => setModal({ type, data });
  const closeModal          = () => setModal({ type: null, data: null });

  const [showEventModal,      setShowEventModal]      = useState(false);
  const [showResetAlertsModal, setShowResetAlertsModal] = useState(false);
  const [showScanView,        setShowScanView]        = useState(false);
  const [showLivePOBModal,    setShowLivePOBModal]    = useState(false);
  const [showDeptQuickModal,  setShowDeptQuickModal]  = useState(false);
  const [scanZoneManagementOnly, setScanZoneManagementOnly] = useState(false);
  const [confirmConfig, setConfirmConfig] = useState(null);
  // ActionFormModal — null when closed, { siteId?, incidentId? } when open
  const [actionFormConfig, setActionFormConfig] = useState(null);

  // ── Load all data from API on mount ──
  useEffect(() => {
    async function load() {
      try {
        const [co, si, zo, em, al, tx, ev, vc, vr, vcard, depts, rls, scheds, inc, act] = await Promise.all([
          companiesApi.list(),
          sitesApi.list(),
          zonesApi.list(),
          employeesApi.list(),
          alertsApi.list(),
          transactionsApi.list(),
          eventsApi.listActive(),
          visitorsApi.listVisitorCompanies().catch(() => []),
          visitorsApi.listVisitRequests().catch(() => []),
          visitorsApi.listVisitorCards().catch(() => []),
          departmentsApi.list().catch(() => []),
          rolesApi.list().catch(() => []),
          taApi.listSchedules().catch(() => []),
          incidentsApi.list().catch(() => []),
          actionsApi.list().catch(() => []),
        ]);
        setCompanies(co);
        setSites(si);
        setZones(zo);
        setEmployees(em);
        setAlerts(al);
        setTransactions(tx);
        setActiveEvents(ev);
        setVisitorCompanies(vc);
        setVisitRequests(vr);
        setVisitorCards(vcard);
        setDepartments(depts);
        setRoles(rls);
        setSchedules(scheds);
        setIncidents(inc);
        setActions(act);
      } catch (err) {
        console.error('Failed to load data:', err);
      } finally {
        setDataLoading(false);
      }
    }
    load();
  }, []);

  // ── Refresh helpers ──
  const refreshEmployees = useCallback(async () => {
    const em = await employeesApi.list();
    setEmployees(em);
  }, []);
  const refreshCompanies = useCallback(async () => {
    const co = await companiesApi.list();
    setCompanies(co);
  }, []);
  const refreshAlerts = useCallback(async () => {
    const al = await alertsApi.list();
    setAlerts(al);
  }, []);
  const refreshTransactions = useCallback(async () => {
    const tx = await transactionsApi.list();
    setTransactions(tx);
  }, []);
  const refreshEvents = useCallback(async () => {
    const ev = await eventsApi.listActive();
    setActiveEvents(ev);
  }, []);
  const refreshVisitorCards = useCallback(async () => {
    const vc = await visitorsApi.listVisitorCards();
    setVisitorCards(vc);
  }, []);
  const refreshIncidents = useCallback(async () => {
    const inc = await incidentsApi.list();
    setIncidents(inc);
  }, []);
  const refreshActions = useCallback(async () => {
    const act = await actionsApi.list();
    setActions(act);
  }, []);

  // ── Poll live data every 30 s so QR scan updates appear without a page refresh ──
  useEffect(() => {
    const id = setInterval(async () => {
      try {
        await Promise.all([
          refreshEmployees(),
          refreshAlerts(),
          refreshTransactions(),
          refreshEvents(),
          refreshVisitorCards(),
          refreshIncidents(),
          refreshActions(),
        ]);
      } catch (_) {}
    }, 30000);
    return () => clearInterval(id);
  }, [refreshEmployees, refreshAlerts, refreshTransactions, refreshEvents, refreshVisitorCards, refreshIncidents, refreshActions]);

  // ── CRUD: Company ──
  async function handleSaveCompany(data) {
    try {
      if (data.id) {
        const updated = await companiesApi.update(data.id, data);
        setCompanies(p => p.map(c => c.id === data.id ? updated : c));
      } else {
        const created = await companiesApi.create(data);
        setCompanies(p => [...p, created]);
      }
    } catch (err) { console.error('Save company failed:', err); throw err; }
  }

  async function handleRemoveCompany(id) {
    setConfirmConfig({
      title: 'Remove Company',
      message: 'This will permanently remove the company and ALL its sites, zones, and employees. This cannot be undone.',
      onConfirm: async () => {
        await companiesApi.remove(id);
        setCompanies(p => p.filter(c => c.id !== id));
        const siteIds = sites.filter(s => s.companyId === id).map(s => s.id);
        setSites(p => p.filter(s => s.companyId !== id));
        setZones(p => p.filter(z => z.companyId !== id));
        setEmployees(p => p.filter(e => e.companyId !== id));
        setActiveEvents(p => { const n = { ...p }; siteIds.forEach(s => delete n[s]); return n; });
        if (selectedSiteId && siteIds.includes(selectedSiteId)) { setCurrentView('map'); setSelectedSiteId(null); }
      },
    });
  }

  // ── CRUD: Site ──
  async function handleSaveSite(data) {
    try {
      if (data.id) {
        const updated = await sitesApi.update(data.id, data);
        setSites(p => p.map(s => s.id === data.id ? updated : s));
      } else {
        const created = await sitesApi.create(data);
        setSites(p => [...p, created]);
      }
    } catch (err) { console.error('Save site failed:', err); }
  }

  async function handleRemoveSite(id) {
    setConfirmConfig({
      title: 'Remove Site',
      message: 'This will remove the site and all its zones. Employees will be marked as off-site.',
      onConfirm: async () => {
        await sitesApi.remove(id);
        setSites(p => p.filter(s => s.id !== id));
        setZones(p => p.filter(z => z.siteId !== id));
        setEmployees(p => p.map(e => e.currentSiteId === id ? { ...e, currentSiteId: null, currentZoneId: null } : e));
        setActiveEvents(p => { const n = { ...p }; delete n[id]; return n; });
        if (selectedSiteId === id) { setCurrentView('map'); setSelectedSiteId(null); }
      },
    });
  }

  // ── CRUD: Zone ──
  async function handleSaveZone(data) {
    try {
      if (data.id) {
        const updated = await zonesApi.update(data.id, data);
        setZones(p => p.map(z => z.id === data.id ? updated : z));
      } else {
        const created = await zonesApi.create(data);
        setZones(p => [...p, created]);
      }
    } catch (err) { console.error('Save zone failed:', err); }
  }

  async function handleRemoveZone(id) {
    setConfirmConfig({
      title: 'Remove Zone',
      message: 'Employees currently in this zone will remain on-site but unassigned to a zone.',
      onConfirm: async () => {
        await zonesApi.remove(id);
        setZones(p => p.filter(z => z.id !== id));
        setEmployees(p => p.map(e => e.currentZoneId === id ? { ...e, currentZoneId: null } : e));
        if (selectedZoneId === id) { setSelectedZoneId(null); setCurrentView('site'); }
      },
    });
  }

  async function handleDeactivateZone(id) {
    setConfirmConfig({
      title: 'Deactivate Zone',
      message: 'No new scans will be accepted. You can delete the zone afterwards.',
      onConfirm: async () => {
        try {
          await zonesApi.deactivate(id);
          setZones(p => p.map(z => z.id === id ? { ...z, isActive: false } : z));
        } catch (err) { console.error('Deactivate zone failed:', err); }
      },
    });
  }

  // ── CRUD: Employee ──
  async function handleSaveEmployee(data) {
    if (data.id) {
      const updated = await employeesApi.update(data.id, data);
      setEmployees(p => p.map(e => e.id === data.id ? updated : e));
    } else {
      const created = await employeesApi.create(data);
      setEmployees(p => [...p, created]);
    }
  }

  async function handleToggleActive(id) {
    const emp = employees.find(e => e.id === id);
    if (!emp) return;
    const updated = await employeesApi.update(id, { ...emp, isActive: !emp.isActive });
    setEmployees(p => p.map(e => e.id === id ? updated : e));
  }

  async function handleRemoveEmployee(id) {
    setConfirmConfig({
      title: 'Archive Employee',
      message: 'The employee will be archived. Their QR code will stop working immediately. Their transaction history is preserved and they can be restored later from the Archived Employees page.',
      onConfirm: async () => {
        await employeesApi.remove(id);
        setEmployees(p => p.filter(e => e.id !== id));
        await refreshAlerts();
        if (selectedEmployeeId === id) { setCurrentView('site'); setSelectedEmployeeId(null); }
      },
    });
  }

  // ── Check in / out ──
  async function handleCheckIn(employeeId, { siteId, zoneId }) {
    const updated = await employeesApi.checkin(employeeId, { siteId, zoneId });
    setEmployees(p => p.map(e => e.id === employeeId ? updated : e));
    await refreshTransactions();
  }

  async function handleCheckOut(employeeId) {
    const updated = await employeesApi.checkout(employeeId);
    setEmployees(p => p.map(e => e.id === employeeId ? updated : e));
    await refreshTransactions();
  }

  // ── Status actions ──
  async function handleMarkSafe(id) {
    const updated = await employeesApi.markSafe(id);
    setEmployees(p => p.map(e => e.id === id ? updated : e));
    await refreshTransactions();
  }

  async function handleMarkEvacuated(id) {
    const updated = await employeesApi.markEvacuated(id);
    setEmployees(p => p.map(e => e.id === id ? updated : e));
    await refreshTransactions();
  }

  // ── QR Scan ──
  async function handleScan({ zoneId, employeeId, visitorCardId, action, emergencyType, gps }) {
    const result = await scanApi.scan({ zoneId, employeeId, visitorCardId, action, emergencyType, gps });
    if (visitorCardId) {
      setVisitorCards(p => p.map(vc => vc.id === visitorCardId ? (result.visitorCard || vc) : vc));
    } else {
      setEmployees(p => p.map(e => e.id === employeeId ? (result.employee || e) : e));
    }
    if (action === 'NEED_HELP') await refreshAlerts();
    await refreshTransactions();
  }

  // ── LivePOB ──
  async function handleLivePOBScan({ employeeId, action, emergencyType, gps }) {
    const result = await scanApi.livepob({ employeeId, action, emergencyType, gps });
    setEmployees(p => p.map(e => e.id === employeeId ? result.employee : e));
    if (action === 'NEED_HELP') await refreshAlerts();
    await refreshTransactions();
  }

  // ── Events ──
  async function handleStartEvent(data) {
    try {
      await eventsApi.start(data);
      await Promise.all([refreshEvents(), refreshEmployees(), refreshTransactions()]);
    } catch (err) {
      console.error('Start event failed:', err);
      alert(`Failed to start event: ${err.response?.data?.error || err.message}`);
    }
  }

  async function handleBulkResetAlerts(params) {
    const [alertResult] = await Promise.all([
      alertsApi.bulkAcknowledge(params),
      employeesApi.bulkResetStatus(params),
      visitorsApi.bulkResetStatus(params),
    ]);
    await Promise.all([refreshAlerts(), refreshEmployees(), refreshVisitorCards()]);
    return alertResult;
  }

  async function handleEndEvent(siteId) {
    setConfirmConfig({
      title: 'End Event',
      message: 'End the active emergency event? Employee statuses will be frozen.',
      onConfirm: async () => {
        await eventsApi.end(siteId);
        await refreshEvents();
      },
    });
  }

  async function handleResetStatus(siteId) {
    setConfirmConfig({
      title: 'Reset Status',
      message: 'Reset all employees and visitors at this site to NOT_RESPONDED?',
      onConfirm: async () => {
        try {
          await eventsApi.resetStatus(siteId);
          await Promise.all([refreshEmployees(), refreshVisitorCards(), refreshTransactions()]);
        } catch (err) {
          console.error('Reset status failed:', err);
          alert('Reset failed. Please try again.');
        }
      },
    });
  }

  // ── Alerts ──
  async function handleAcknowledge(alertId) {
    const updated = await alertsApi.acknowledge(alertId);
    setAlerts(p => p.map(a => a.id === alertId ? updated : a));
  }

  async function handleUpdateNotes(alertId, notes) {
    const updated = await alertsApi.updateNotes(alertId, notes);
    setAlerts(p => p.map(a => a.id === alertId ? updated : a));
  }

  // ── VMS handlers ──
  function handleVCAdd(vc)    { setVisitorCompanies(p => [...p, vc]); }
  function handleVCUpdate(vc) { setVisitorCompanies(p => p.map(x => x.id === vc.id ? vc : x)); }
  function handleVCRemove(id) { setVisitorCompanies(p => p.filter(x => x.id !== id)); }

  function handleVRUpdate(vr) { setVisitRequests(p => p.map(x => x.id === vr.id ? vr : x)); }
  function handleVRRemove(id) { setVisitRequests(p => p.filter(x => x.id !== id)); }

  function handleCardBulkAdd(cards) { setVisitorCards(p => [...p, ...(Array.isArray(cards) ? cards : [cards])]); }
  function handleCardUpdate(card)   { setVisitorCards(p => p.map(x => x.id === card.id ? card : x)); }
  function handleCardRemove(id)     { setVisitorCards(p => p.filter(x => x.id !== id)); }

  // ── CRUD: Department ──
  function handleDeptAdd(dept)    { setDepartments(p => [...p, dept]); }
  function handleDeptUpdate(dept) { setDepartments(p => p.map(x => x.id === dept.id ? dept : x)); }
  function handleDeptRemove(id)   {
    setDepartments(p => p.filter(x => x.id !== id));
    setEmployees(p => p.map(e => e.departmentId === id ? { ...e, departmentId: null } : e));
  }

  // ── CRUD: Roles ──
  function handleRoleAdd(role)    { setRoles(p => [...p, role]); }
  function handleRoleUpdate(role) { setRoles(p => p.map(x => x.id === role.id ? role : x)); }
  function handleRoleRemove(id)   { setRoles(p => p.filter(x => x.id !== id)); }

  // ── Navigation ──
  const unackCount = alerts.filter(a => !a.acknowledged).length;
  const myOpenTasksCount =
    incidents.filter(i => i.assignedUserId === user?.id && i.status !== 'CLOSED').length +
    actions.filter(a   => a.assignedUserId === user?.id && a.status !== 'COMPLETED').length;
  function navToSite(id)        { setSelectedSiteId(id);         setCurrentView('site'); }
  function navToCompanyMap(id)  { setSelectedCompanyMapId(id);   setCurrentView('companyMap'); }
  function navToSiteMap(id)     { setSelectedSiteId(id);         setCurrentView('siteMap'); }
  function navToZone(id)        { setSelectedZoneId(id);         setCurrentView('zone'); }
  function navToEmployee(id)    { setSelectedEmployeeId(id);     setCurrentView('employee'); }
  function navToTeam()          { setCurrentView('team'); }
  function navToHseCourses()    { setCurrentView('hseCourses'); }

  // ── Derived ──
  const selectedSite     = sites.find(s => s.id === selectedSiteId);
  const selectedZone     = zones.find(z => z.id === selectedZoneId);
  const selectedEmployee = employees.find(e => e.id === selectedEmployeeId);
  const selectedVisitorCard = visitorCards.find(vc => vc.id === selectedVisitorCardId) || null;
  const vcSite = sites.find(s => s.id === selectedVisitorCard?.siteId) || null;
  const vcZone = zones.find(z => z.id === selectedVisitorCard?.currentZoneId) || null;
  const selectedCompany  = companies.find(c => c.id === (selectedSite?.companyId || selectedEmployee?.companyId));
  const siteZones        = zones.filter(z => z.siteId === selectedSiteId);
  const empZone          = zones.find(z => z.id === selectedEmployee?.currentZoneId);
  const empSite          = sites.find(s => s.id === selectedEmployee?.currentSiteId);
  const empCompany       = companies.find(c => c.id === selectedEmployee?.companyId);
  function siteNeedHelp(siteId) { return employees.filter(e => e.currentSiteId === siteId && e.status === 'NEED_HELP').length; }

  if (dataLoading) return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh', background: C.bg }}>
      <div style={{ color: C.textMuted, fontSize: '14px' }}>Loading data…</div>
    </div>
  );

  // ── Render ──
  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100vh', background: C.bg, overflow: 'hidden' }}>
      <TopBar activeEvents={activeEvents} sites={sites} user={user} onLogout={onLogout} onEndEvent={handleEndEvent} />

      <div style={{ display: 'flex', flex: 1, minHeight: 0 }}>
        {/* ── Sidebar ── */}
        <div style={{ width: '240px', background: C.surface, borderRight: `1px solid ${C.border}`, display: 'flex', flexDirection: 'column', flexShrink: 0, overflowY: 'auto' }}>
          <div style={{ padding: '12px 10px 6px' }}>
            <div style={{ fontSize: '10px', color: C.textMuted, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.07em', padding: '4px 8px 8px' }}>Navigation</div>
            {canAccess('map')          && <SidebarItem icon={Globe}     label="Company Map"       active={currentView === 'map'}          onClick={() => setCurrentView('map')} />}
            {canAccess('alerts')       && <SidebarItem icon={Bell}      label="Alerts Log"        badge={unackCount} active={currentView === 'alerts'}    onClick={() => setCurrentView('alerts')} />}
            {canAccess('transactions') && <SidebarItem icon={History}   label="Transaction Log"   active={currentView === 'transactions'} onClick={() => setCurrentView('transactions')} />}
            {canAccess('employees')    && <SidebarItem icon={Users}     label="Employees"         active={currentView === 'employees'}    onClick={() => setCurrentView('employees')} />}
            {canAccess('departments')  && <SidebarItem icon={Layers}    label="Departments"       active={currentView === 'departments'}  onClick={() => setCurrentView('departments')} />}
            {canAccess('roles')        && <SidebarItem icon={Briefcase} label="Roles"             active={currentView === 'roles'}        onClick={() => setCurrentView('roles')} />}
            {canAccess('headcount')    && <SidebarItem icon={FileText}  label="Headcount Report"  active={currentView === 'headcount'}    onClick={() => setCurrentView('headcount')} />}
            <SidebarItem icon={Inbox} label="My Tasks"
              badge={myOpenTasksCount > 0 ? myOpenTasksCount : undefined}
              active={currentView === 'myTasks'}
              onClick={() => setCurrentView('myTasks')} />
          </div>

          {canAccess('hse_team') && (
            <div style={{ padding: '4px 10px 6px' }}>
              <div style={{ fontSize: '10px', color: C.textMuted, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.07em', padding: '4px 8px 8px' }}>HSE</div>
              <SidebarItem icon={Users2} label="Team" active={currentView === 'team'} onClick={navToTeam} />
            </div>
          )}

          <div style={{ padding: '4px 10px 6px' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '4px 8px 8px' }}>
              <span style={{ fontSize: '10px', color: C.textMuted, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.07em' }}>Companies</span>
              <button onClick={() => openModal('company')} style={{ background: 'none', border: 'none', cursor: 'pointer', color: C.blue, fontSize: '18px', lineHeight: 1, padding: '0 2px' }} title="Add Company">+</button>
            </div>

            {companies.map(company => {
              const companySites  = sites.filter(s => s.companyId === company.id);
              const expanded      = expandedCompanies[company.id];
              const companyNeedHelp = employees.filter(e => e.companyId === company.id && e.status === 'NEED_HELP').length;

              return (
                <div key={company.id}>
                  <div onClick={() => setExpandedCompanies(p => ({ ...p, [company.id]: !p[company.id] }))}
                    style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '7px 8px', borderRadius: '8px', cursor: 'pointer', marginBottom: '1px', userSelect: 'none' }}>
                    {expanded ? <ChevronDown size={13} color={C.textMuted} /> : <ChevronRight size={13} color={C.textMuted} />}
                    <Building2 size={13} color={C.textSecondary} />
                    <span style={{ flex: 1, fontSize: '12px', fontWeight: 700, color: C.textPrimary, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{company.name}</span>
                    {companyNeedHelp > 0 && (
                      <span style={{ background: C.red, color: '#fff', fontSize: '10px', fontWeight: 700, padding: '1px 5px', borderRadius: '8px' }}>{companyNeedHelp}</span>
                    )}
                    <button onClick={e => { e.stopPropagation(); navToCompanyMap(company.id); }}
                      style={{ background: 'none', border: 'none', cursor: 'pointer', color: currentView === 'companyMap' && selectedCompanyMapId === company.id ? C.blue : C.textMuted, padding: '0 2px', flexShrink: 0, display: 'flex', alignItems: 'center' }} title="Company Map">
                      <Map size={12} />
                    </button>
                    <button onClick={e => { e.stopPropagation(); openModal('company', { record: company }); }}
                      style={{ background: 'none', border: 'none', cursor: 'pointer', color: C.textMuted, padding: '0 2px', flexShrink: 0, display: 'flex', alignItems: 'center' }} title="Edit Company">
                      <Pencil size={12} />
                    </button>
                    <button onClick={e => { e.stopPropagation(); openModal('site', { preselectedCompanyId: company.id }); }}
                      style={{ background: 'none', border: 'none', cursor: 'pointer', color: C.blue, fontSize: '16px', lineHeight: 1, padding: '0 2px', flexShrink: 0 }} title="Add Site">+</button>
                  </div>

                  {expanded && companySites.map(site => {
                    const siteExpanded = expandedSites[site.id];
                    const showSiteSubNav = canAccess('visitor_companies') || canAccess('visit_requests') || canAccess('visitor_cards') || canAccess('incidents') || canAccess('actions');
                    const siteOpenIncidents = incidents.filter(i => i.siteId === site.id && i.status !== 'CLOSED').length;
                    const siteOpenActions = actions.filter(a => a.siteId === site.id && a.status !== 'COMPLETED').length;
                    return (
                      <div key={site.id}>
                        <div style={{ display: 'flex', alignItems: 'center' }}>
                          {showSiteSubNav && (
                            <button onClick={e => { e.stopPropagation(); setExpandedSites(p => ({ ...p, [site.id]: !p[site.id] })); }}
                              style={{ background: 'none', border: 'none', cursor: 'pointer', color: C.textMuted, padding: '0 2px 0 12px', flexShrink: 0, display: 'flex', alignItems: 'center' }} title={siteExpanded ? 'Collapse' : 'Expand'}>
                              {siteExpanded ? <ChevronDown size={11} /> : <ChevronRight size={11} />}
                            </button>
                          )}
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <SidebarItem icon={MapPin} label={site.name}
                              badge={siteNeedHelp(site.id)}
                              active={currentView === 'site' && selectedSiteId === site.id}
                              onClick={() => navToSite(site.id)} indent />
                          </div>
                          <button onClick={e => { e.stopPropagation(); navToSiteMap(site.id); }}
                            style={{ background: 'none', border: 'none', cursor: 'pointer', color: currentView === 'siteMap' && selectedSiteId === site.id ? C.blue : C.textMuted, padding: '4px 6px', flexShrink: 0, display: 'flex', alignItems: 'center' }} title="Site Map">
                            <Map size={11} />
                          </button>
                        </div>
                        {siteExpanded && (
                          <div style={{ paddingLeft: '20px' }}>
                            {canAccess('visitor_companies') && (
                              <SidebarItem icon={UserCheck} label="Visitor Companies"
                                active={currentView === 'visitorCompanies' && selectedVisitorsSiteId === site.id}
                                onClick={() => { setCurrentView('visitorCompanies'); setSelectedVisitorsSiteId(site.id); }}
                                indent />
                            )}
                            {canAccess('visit_requests') && (
                              <SidebarItem icon={ClipboardList} label="Visit Requests"
                                badge={visitRequests.filter(v => v.siteId === site.id && v.status === 'PENDING').length}
                                active={currentView === 'visitRequests' && selectedVisitorsSiteId === site.id}
                                onClick={() => { setCurrentView('visitRequests'); setSelectedVisitorsSiteId(site.id); }}
                                indent />
                            )}
                            {canAccess('visitor_cards') && (
                              <SidebarItem icon={CreditCard} label="Visitor Cards"
                                active={currentView === 'visitorCards' && selectedVisitorsSiteId === site.id}
                                onClick={() => { setCurrentView('visitorCards'); setSelectedVisitorsSiteId(site.id); }}
                                indent />
                            )}
                            {canAccess('incidents') && (
                              <SidebarItem icon={AlertOctagon} label="Incidents"
                                badge={siteOpenIncidents > 0 ? siteOpenIncidents : undefined}
                                active={currentView === 'incidents' && selectedIncidentsSiteId === site.id}
                                onClick={() => { setCurrentView('incidents'); setSelectedIncidentsSiteId(site.id); }}
                                indent />
                            )}
                            {canAccess('actions') && (
                              <SidebarItem icon={CheckSquare} label="Actions"
                                badge={siteOpenActions > 0 ? siteOpenActions : undefined}
                                active={currentView === 'actions' && selectedActionsSiteId === site.id}
                                onClick={() => { setCurrentView('actions'); setSelectedActionsSiteId(site.id); }}
                                indent />
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                  {expanded && company.timeAttendanceEnabled && canAccess('time_attendance') && (
                    <SidebarItem icon={Clock} label="Time & Attendance"
                      active={currentView === 'timeAttendance' && selectedTACompanyId === company.id}
                      onClick={() => { setCurrentView('timeAttendance'); setSelectedTACompanyId(company.id); }}
                      indent />
                  )}
                  {expanded && company.hseTrainingEnabled && canAccess('hse_courses') && (
                    <SidebarItem icon={ShieldCheck} label="HSE Courses"
                      active={currentView === 'hseCourses' && selectedHseCompanyId === company.id}
                      onClick={() => { setCurrentView('hseCourses'); setSelectedHseCompanyId(company.id); }}
                      indent />
                  )}
                  {expanded && ['ADMIN', 'SUPER_ADMIN'].includes(user.role) && (
                    <SidebarItem icon={Archive} label="Archived Employees"
                      active={currentView === 'archivedEmployees' && selectedArchivedCompanyId === company.id}
                      onClick={() => { setCurrentView('archivedEmployees'); setSelectedArchivedCompanyId(company.id); }}
                      indent />
                  )}
                </div>
              );
            })}
          </div>

          <div style={{ padding: '4px 10px 6px', borderTop: `1px solid ${C.border}` }}>
            <div style={{ fontSize: '10px', color: C.textMuted, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.07em', padding: '8px 8px 6px' }}>History</div>
            <SidebarItem icon={SearchIcon} label="Find Relevant History" active={currentView === 'historySearch'} onClick={() => setCurrentView('historySearch')} />
            <SidebarItem icon={BookOpen}   label="Knowledge Base"        active={currentView === 'kbBrowser'}     onClick={() => setCurrentView('kbBrowser')} />
            {['ADMIN', 'SUPER_ADMIN'].includes(user.role) && (
              <SidebarItem icon={FolderOpen} label="Upload Manager"      active={currentView === 'historyUpload'} onClick={() => setCurrentView('historyUpload')} />
            )}
          </div>

          {['ADMIN', 'SUPER_ADMIN'].includes(user.role) && (
            <div style={{ padding: '4px 10px 6px', borderTop: `1px solid ${C.border}` }}>
              <div style={{ fontSize: '10px', color: C.textMuted, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.07em', padding: '8px 8px 6px' }}>Admin</div>
              <SidebarItem icon={Shield}    label="Access Rights"        active={currentView === 'access_rights'}    onClick={() => setCurrentView('access_rights')} />
              <SidebarItem icon={Plug}      label="Integrations"          active={currentView === 'integrations'}      onClick={() => setCurrentView('integrations')} />
              <SidebarItem icon={Sparkles}  label="HSE AI Settings"       active={currentView === 'hseAiSettings'}     onClick={() => setCurrentView('hseAiSettings')} />
            </div>
          )}

          {user.role === 'SUPER_ADMIN' && (
            <div style={{ padding: '4px 10px 6px', borderTop: `1px solid ${C.border}` }}>
              <div style={{ fontSize: '10px', color: C.textMuted, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.07em', padding: '8px 8px 6px' }}>Platform</div>
              <SidebarItem icon={Settings2} label="Platform Admin" active={currentView === 'superAdmin'} onClick={() => setCurrentView('superAdmin')} />
            </div>
          )}

          <div style={{ marginTop: user.role === 'SUPER_ADMIN' ? '0' : 'auto', padding: '10px', borderTop: `1px solid ${C.border}` }}>
            <div style={{ fontSize: '10px', color: C.textMuted, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.07em', padding: '4px 4px 8px' }}>Actions</div>
            <button onClick={() => setShowEventModal(true)} style={{ width: '100%', padding: '9px 12px', borderRadius: '8px', border: `1px solid ${C.red}44`, background: C.red+'22', color: C.red, fontSize: '12px', fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit', display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px' }}>
              <Radio size={13} />Start Event
            </button>
            <button onClick={() => setShowResetAlertsModal(true)} style={{ width: '100%', padding: '9px 12px', borderRadius: '8px', border: `1px solid ${C.amber}44`, background: C.amber+'22', color: C.amber, fontSize: '12px', fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit', display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px' }}>
              <CheckCheck size={13} />Reset Alerts
            </button>
            <button onClick={() => setShowScanView(true)} style={{ width: '100%', padding: '9px 12px', borderRadius: '8px', border: `1px solid ${C.blue}44`, background: C.blue+'22', color: C.blue, fontSize: '12px', fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit', display: 'flex', alignItems: 'center', gap: '8px' }}>
              <QrCode size={13} />Test QR Scan
            </button>
          </div>
        </div>

        {/* ── Main Content ── */}
        <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          {currentView === 'map' && (
            <CompanyMapView companies={companies} sites={sites} zones={zones}
              employees={employees} visitorCards={visitorCards} alerts={alerts} activeEvents={activeEvents}
              onSiteClick={navToSite} onAcknowledge={handleAcknowledge} />
          )}
          {currentView === 'companyMap' && selectedCompanyMapId && (
            <CompanyScopeMapView
              companyId={selectedCompanyMapId}
              companies={companies} sites={sites} zones={zones}
              employees={employees} visitorCards={visitorCards}
              alerts={alerts} activeEvents={activeEvents}
              onBack={() => setCurrentView('map')}
              onSiteMapClick={navToSiteMap}
              onAcknowledge={handleAcknowledge}
            />
          )}
          {currentView === 'siteMap' && selectedSiteId && (
            <SiteScopeMapView
              siteId={selectedSiteId}
              companies={companies} sites={sites} zones={zones}
              employees={employees} visitorCards={visitorCards}
              alerts={alerts} activeEvents={activeEvents}
              onBack={() => navToSite(selectedSiteId)}
              onAcknowledge={handleAcknowledge}
            />
          )}
          {currentView === 'alerts' && (
            <AlertsView alerts={alerts} companies={companies} sites={sites} zones={zones}
              onAcknowledge={handleAcknowledge} onUpdateNotes={handleUpdateNotes} />
          )}
          {currentView === 'employees' && (
            <EmployeesView companies={companies} sites={sites} zones={zones} employees={employees}
              onAdd={() => openModal('employee')}
              onEdit={emp => openModal('employee', { record: emp })}
              onRemove={id => handleRemoveEmployee(id)}
              onToggleActive={handleToggleActive}
              onCheckIn={handleCheckIn} onCheckOut={handleCheckOut}
              onEmployeeClick={navToEmployee} />
          )}
          {currentView === 'headcount' && (
            <HeadcountView companies={companies} sites={sites} zones={zones} employees={employees} />
          )}
          {currentView === 'transactions' && (
            <TransactionLogView transactions={transactions} companies={companies} sites={sites} zones={zones} />
          )}
          {currentView === 'site' && selectedSite && (
            <SiteView site={selectedSite} company={selectedCompany} zones={siteZones}
              employees={employees} visitorCards={visitorCards} alerts={alerts} activeEvents={activeEvents} transactions={transactions}
              onBack={() => setCurrentView('map')}
              onEdit={() => openModal('site', { record: selectedSite })}
              onRemove={() => handleRemoveSite(selectedSiteId)}
              onStartEvent={() => setShowEventModal(true)}
              onEndEvent={handleEndEvent} onResetStatus={() => handleResetStatus(selectedSiteId)}
              onEmployeeClick={navToEmployee} onZoneClick={navToZone}
              onEditEmployee={emp => openModal('employee', { record: emp })}
              onRemoveEmployee={id => handleRemoveEmployee(id)}
              onSimulateScan={emp => { setSelectedEmployeeId(emp.id); setShowScanView(true); }}
              onVisitorClick={id => { setSelectedVisitorCardId(id); setCurrentView('visitor'); }}
              onAddZone={() => openModal('zone', { preselectedSiteId: selectedSiteId })}
              onAddEmployee={() => openModal('employee', { preselectedCompanyId: selectedSite.companyId, preselectedSiteId: selectedSiteId })}
              onViewMap={() => navToSiteMap(selectedSiteId)}
              departments={departments}
              onAddDepartment={() => setShowDeptQuickModal(true)} />
          )}
          {currentView === 'zone' && selectedZone && (
            <ZoneView zone={selectedZone} zones={zones}
              site={sites.find(s => s.id === selectedZone.siteId)}
              company={companies.find(c => c.id === selectedZone.companyId)}
              employees={employees}
              visitorCards={visitorCards}
              onBack={() => setCurrentView('site')}
              onEditZone={() => openModal('zone', { record: selectedZone })}
              onAddZone={() => openModal('zone', { preselectedSiteId: selectedZone.siteId, preselectedParentZoneId: selectedZone.id })}
              onSubZoneClick={navToZone}
              onDeactivate={() => handleDeactivateZone(selectedZoneId)}
              onDeleteZone={() => handleRemoveZone(selectedZoneId)}
              user={user} />
          )}
          {currentView === 'visitorCompanies' && (
            <VisitorCompaniesView
              visitorCompanies={visitorCompanies}
              sites={sites}
              scopedSiteId={selectedVisitorsSiteId}
              onAdd={handleVCAdd}
              onUpdate={handleVCUpdate}
              onRemove={handleVCRemove}
            />
          )}
          {currentView === 'visitRequests' && (
            <VisitRequestsView
              visitRequests={visitRequests}
              sites={sites}
              scopedSiteId={selectedVisitorsSiteId}
              onUpdate={handleVRUpdate}
              onRemove={handleVRRemove}
            />
          )}
          {currentView === 'visitorCards' && (
            <VisitorCardsView
              visitorCards={visitorCards}
              sites={sites}
              zones={zones}
              visitRequests={visitRequests}
              scopedSiteId={selectedVisitorsSiteId}
              onBulkAdd={handleCardBulkAdd}
              onUpdate={handleCardUpdate}
              onRemove={handleCardRemove}
            />
          )}
          {currentView === 'departments' && (
            <DepartmentsView
              departments={departments}
              companies={companies}
              sites={sites}
              onAdd={handleDeptAdd}
              onUpdate={handleDeptUpdate}
              onRemove={handleDeptRemove}
            />
          )}
          {currentView === 'roles' && (
            <RolesView roles={roles} onAdd={handleRoleAdd} onUpdate={handleRoleUpdate} onRemove={handleRoleRemove} />
          )}
          {currentView === 'timeAttendance' && (
            <TimeAttendanceView
              key={selectedTACompanyId}
              companyId={selectedTACompanyId}
              companies={companies}
              sites={sites}
              employees={employees}
              schedules={schedules}
              onScheduleCreated={s => setSchedules(p => [...p, s])}
              onScheduleUpdated={s => setSchedules(p => p.map(x => x.id === s.id ? s : x))}
              onScheduleDeleted={id => setSchedules(p => p.filter(x => x.id !== id))}
            />
          )}
          {currentView === 'team' && (
            <TeamView companyId={companies[0]?.id} sites={sites.filter(s => s.companyId === companies[0]?.id)} />
          )}
          {currentView === 'hseCourses' && (
            <HseCoursesView
              companyId={selectedHseCompanyId}
              siteId={null}
              sites={sites.filter(s => s.companyId === selectedHseCompanyId)}
            />
          )}
          {currentView === 'incidents' && selectedIncidentsSiteId && (
            <IncidentsView
              key={`${selectedIncidentsSiteId}:${pendingIncidentId || ''}`}
              siteId={selectedIncidentsSiteId}
              site={sites.find(s => s.id === selectedIncidentsSiteId)}
              zones={zones.filter(z => z.siteId === selectedIncidentsSiteId)}
              incidents={incidents.filter(i => i.siteId === selectedIncidentsSiteId)}
              actions={actions.filter(a => a.siteId === selectedIncidentsSiteId)}
              currentUserId={user?.id}
              initialSelectedId={pendingIncidentId}
              onRefresh={async () => { await refreshIncidents(); await refreshActions(); }}
              onOpenActionForm={(opts) => setActionFormConfig(opts)}
            />
          )}
          {currentView === 'actions' && selectedActionsSiteId && (
            <ActionsView
              key={`${selectedActionsSiteId}:${pendingActionId || ''}`}
              siteId={selectedActionsSiteId}
              site={sites.find(s => s.id === selectedActionsSiteId)}
              zones={zones.filter(z => z.siteId === selectedActionsSiteId)}
              actions={actions.filter(a => a.siteId === selectedActionsSiteId)}
              incidents={incidents.filter(i => i.siteId === selectedActionsSiteId)}
              currentUserId={user?.id}
              initialSelectedId={pendingActionId}
              onRefresh={refreshActions}
              onCreateClick={() => setActionFormConfig({ siteId: selectedActionsSiteId })}
            />
          )}
          {currentView === 'myTasks' && (
            <MyTasksView
              incidents={incidents}
              actions={actions}
              currentUserId={user?.id}
              onOpenIncident={(id, siteId) => {
                setPendingIncidentId(id);
                setSelectedIncidentsSiteId(siteId);
                setCurrentView('incidents');
              }}
              onOpenAction={(id, siteId) => {
                setPendingActionId(id);
                setSelectedActionsSiteId(siteId);
                setCurrentView('actions');
              }}
            />
          )}
          {currentView === 'historyUpload' && (
            <UploadManagerView sites={sites} />
          )}
          {currentView === 'historySearch' && (
            <HistorySearchView sites={sites} zones={zones} onPromoted={() => {}} />
          )}
          {currentView === 'kbBrowser' && (
            <KBBrowserView sites={sites} zones={zones} />
          )}
          {currentView === 'hseAiSettings' && (
            <HseAiSettingsView />
          )}
          {currentView === 'superAdmin' && (
            <SuperAdminView />
          )}
          {currentView === 'access_rights' && (
            <AccessRightsView />
          )}
          {currentView === 'integrations' && (
            <IntegrationsView onSyncComplete={async () => { await Promise.all([refreshCompanies(), refreshEmployees()]); }} />
          )}
          {currentView === 'archivedEmployees' && (
            <ArchivedEmployeesView
              key={selectedArchivedCompanyId}
              companies={companies}
              companyId={selectedArchivedCompanyId}
              onRestored={refreshEmployees}
            />
          )}
          {currentView === 'visitor' && selectedVisitorCard && (
            <VisitorCardDetailView
              visitorCard={selectedVisitorCard}
              site={vcSite}
              zone={vcZone}
              zones={zones}
              onBack={() => { setCurrentView(selectedSiteId ? 'site' : 'map'); setSelectedVisitorCardId(null); }}
              onSimulateScan={() => { setScanZoneManagementOnly(true); setShowScanView(true); }}
              onUpdateSettings={async (id, body) => {
                await visitorsApi.updateVisitorCardSettings(id, body);
                await refreshVisitorCards();
              }}
            />
          )}
          {currentView === 'employee' && selectedEmployee && (
            <EmployeeDetailView employee={selectedEmployee} company={empCompany} site={empSite} zone={empZone}
              alerts={alerts.filter(a => a.employeeId === selectedEmployeeId)}
              onBack={() => setCurrentView(selectedSiteId ? 'site' : 'map')}
              onEdit={() => openModal('employee', { record: selectedEmployee })}
              onRemove={() => handleRemoveEmployee(selectedEmployeeId)}
              onSimulateLivePOB={() => setShowLivePOBModal(true)}
              onMarkEvacuated={() => handleMarkEvacuated(selectedEmployeeId)}
              onSimulateScan={() => { setScanZoneManagementOnly(true); setShowScanView(true); }}
              onAcknowledge={handleAcknowledge} />
          )}
        </div>
      </div>

      {/* ── Modals ── */}
      {showDeptQuickModal && selectedSite && (
        <DepartmentFormModal
          data={{ companyId: selectedSite.companyId, siteId: selectedSite.id }}
          companies={companies}
          sites={sites}
          onSave={dept => { handleDeptAdd(dept); setShowDeptQuickModal(false); }}
          onClose={() => setShowDeptQuickModal(false)} />
      )}
      {modal.type === 'company' && (
        <CompanyFormModal company={modal.data?.record} employees={employees}
          onSave={handleSaveCompany}
          onClose={closeModal} />
      )}
      {modal.type === 'site' && (
        <SiteFormModal site={modal.data?.record} companies={companies} employees={employees}
          preselectedCompanyId={modal.data?.preselectedCompanyId}
          onSave={handleSaveSite}
          onClose={closeModal} />
      )}
      {modal.type === 'zone' && (
        <ZoneFormModal zone={modal.data?.record} zones={zones} sites={sites}
          preselectedSiteId={modal.data?.preselectedSiteId}
          preselectedParentZoneId={modal.data?.preselectedParentZoneId}
          onSave={handleSaveZone}
          onClose={closeModal} />
      )}
      {modal.type === 'employee' && (
        <EmployeeFormModal employee={modal.data?.record} companies={companies} sites={sites}
          departments={departments} roles={roles} schedules={schedules}
          preselectedCompanyId={modal.data?.preselectedCompanyId}
          preselectedSiteId={modal.data?.preselectedSiteId}
          onSave={handleSaveEmployee}
          onClose={closeModal} />
      )}
      {showEventModal && (
        <EventModal companies={companies} sites={sites} zones={zones}
          onStart={handleStartEvent} onClose={() => setShowEventModal(false)} />
      )}
      {showResetAlertsModal && (
        <ResetAlertsModal companies={companies} sites={sites}
          onReset={handleBulkResetAlerts} onClose={() => setShowResetAlertsModal(false)} />
      )}
      {showScanView && (
        <QRScanView zones={zones} employees={employees} sites={sites} companies={companies}
          activeEvents={activeEvents}
          zoneManagementOnly={scanZoneManagementOnly}
          preselectedEmployee={scanZoneManagementOnly && selectedEmployee && !selectedVisitorCardId ? selectedEmployee : null}
          preselectedVisitorCard={scanZoneManagementOnly && selectedVisitorCardId ? visitorCards.find(vc => vc.id === selectedVisitorCardId) || null : null}
          onScan={handleScan}
          onClose={() => { setShowScanView(false); setScanZoneManagementOnly(false); }} />
      )}
      {showLivePOBModal && selectedEmployee && (
        <LivePOBModal employee={selectedEmployee} activeEvents={activeEvents}
          sites={sites} zones={zones} companies={companies}
          onScan={handleLivePOBScan} onClose={() => setShowLivePOBModal(false)} />
      )}
      {confirmConfig && (
        <ConfirmModal title={confirmConfig.title} message={confirmConfig.message}
          onConfirm={confirmConfig.onConfirm} onClose={() => setConfirmConfig(null)} />
      )}
      {actionFormConfig && (
        <ActionFormModal
          sites={sites}
          zones={zones}
          incidents={incidents}
          preselectedSiteId={actionFormConfig.siteId}
          preselectedIncidentId={actionFormConfig.incidentId}
          onCreated={async () => { await refreshActions(); }}
          onClose={() => setActionFormConfig(null)}
        />
      )}
    </div>
  );
}
